/**
 * Consolidation and intercompany elimination (ARCHITECTURE §4). The raw group
 * trial balance always ties (it is a sum of balanced entries). On top of that,
 * this module identifies intercompany balances that should eliminate against
 * each other and surfaces any *unmatched* residual explicitly — rather than
 * letting it hide inside the group totals.
 */
import type { Account, Currency, Entity, JournalEntry, Money } from "./types";
import { add, subtract, zero, abs } from "./money";
import { buildLedger } from "./ledger";

/** Intercompany account pairs that should net to zero across the group. */
const IC_PAIRS: readonly (readonly [string, string])[] = [
  ["1500", "2500"], // Intercompany receivable ↔ payable
  ["4900", "6500"], // Intercompany income ↔ expense
];

export interface UnmatchedItem {
  readonly label: string;
  readonly amount: Money;
}

export interface ConsolidationView {
  readonly entityCount: number;
  /** Total intercompany value eliminated (the matched portion). */
  readonly eliminations: Money;
  /** Residual intercompany items that don't net — flagged for review. */
  readonly unmatched: readonly UnmatchedItem[];
}

export function consolidate(
  entries: readonly JournalEntry[],
  accounts: readonly Account[],
  entities: readonly Entity[],
  base: Currency,
): ConsolidationView {
  const ledger = buildLedger(entries, accounts, base);
  const nameByCode = new Map(accounts.map((a) => [a.code, a.name]));

  let eliminations = zero(base);
  const unmatched: UnmatchedItem[] = [];

  for (const [debitSide, creditSide] of IC_PAIRS) {
    const a = ledger.get(debitSide)?.balance ?? zero(base);
    const b = ledger.get(creditSide)?.balance ?? zero(base);
    // Both carry natural (positive) balances; the matched portion is the min.
    const matched: Money = {
      amount: Math.min(a.amount, b.amount),
      currency: base,
    };
    eliminations = add(eliminations, matched);
    const residual = subtract(a, b);
    if (residual.amount !== 0) {
      const label =
        residual.amount > 0
          ? nameByCode.get(debitSide) ?? debitSide
          : nameByCode.get(creditSide) ?? creditSide;
      unmatched.push({ label, amount: abs(residual) });
    }
  }

  return { entityCount: entities.length, eliminations, unmatched };
}
